import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { counterpartyApi, API_BASE_URL } from '../services/api';
import { fetchBankDetailsByCode } from '../services/paymentApi';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import TbillSellDealModal from '../components/TbillSellDealModal';
import { computeTbillPricing } from '../utils/tbillPricing';
import { DATE_INPUT_MIN, DATE_INPUT_MAX, isDateInRange } from '../utils/dateBounds';
import RecentDealsDateFilter from '../components/RecentDealsDateFilter';
import { useRecentDealsFilterDate } from '../hooks/useRecentDealsFilterDate';
import { filterDealsByFilterDate } from '../utils/recentDealsDate';
import './TbillPage.css';

/** Normalize API date to YYYY-MM-DD for <input type="date" /> */
function toInputDate(val) {
  if (!val) return '';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    return val.slice(0, 10);
  }
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const FixedIncomeTbillPage = ({ allowedTabs, title }) => {
  const getInitialFormState = () => {
    const today = new Date();
    const currentDate = today.toISOString().split('T')[0];

    return {
      tradeDate: currentDate,
      valueDate: currentDate,
      transactionType: '',
      counterparty: '',
      dealNumber: '',
      isin: '',
      faceValue: '',
      yield: '',
      cleanPrice: '',
      dirtyPrice: '',
      broker: '',
      // Default trading defaults per ops convention.
      portfolio: 'Sherwood',
      strategy: 'Sherwood',
      custodian: 'Seylan Bank',
      settlementMode: '',
      brokerage: '',
      currency: 'LKR',
      settlementAmount: '',
      issueDate: '',
      maturityDate: '',
      priceCalculationFormula: ''
    };
  };

  const [form, setForm] = useState(getInitialFormState());

  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [counterparties, setCounterparties] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [settlementModes, setSettlementModes] = useState([]);
  const [isinList, setIsinList] = useState([]);
  const [selectedBankDetails, setSelectedBankDetails] = useState({
    bank_name: '',
    bank_account_number: '',
    bank_branch: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdTbillDealId, setCreatedTbillDealId] = useState(null);
  const [createdDealNumber, setCreatedDealNumber] = useState('');
  const [showTbillDocumentSection, setShowTbillDocumentSection] = useState(false);
  const [selectedTbillDealForDocuments, setSelectedTbillDealForDocuments] = useState(null);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const { filterDateYmd: recentDealsFilterDate, setFilterDateYmd: setRecentDealsFilterDate, loading: recentDealsFilterLoading } = useRecentDealsFilterDate();
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellDeals, setSellDeals] = useState([]);
  const [portfolioFromSellModal, setPortfolioFromSellModal] = useState(false);

  const transactionTypeOptions = [
    { value: 'Buy', label: 'BUY' },
    { value: 'Sell', label: 'SELL' }
  ];

  const currencyOptions = [
    { value: 'LKR', label: 'LKR' },
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' }
  ];

  const pricing = useMemo(
    () =>
      computeTbillPricing({
        valueDateYmd: form.valueDate,
        maturityDateYmd: form.maturityDate,
        faceValue: form.faceValue,
        annualDiscountRatePercent: form.yield
      }),
    [form.valueDate, form.maturityDate, form.faceValue, form.yield]
  );

  const fetchRecentTransactions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/tbill/recent`);
      setRecentTransactions(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch recent T-Bill transactions:', err);
    }
  };

  const filteredRecentTransactions = useMemo(
    () => filterDealsByFilterDate(recentTransactions, recentDealsFilterDate),
    [recentTransactions, recentDealsFilterDate]
  );

  const populateTbillFormFromTransaction = (transaction) => {
    setForm({
      ...getInitialFormState(),
      tradeDate: toInputDate(transaction.trade_date || transaction.tradeDate),
      valueDate: toInputDate(transaction.value_date || transaction.valueDate),
      transactionType: transaction.transaction_type || transaction.transactionType || '',
      counterparty: transaction.counterparty || '',
      dealNumber: transaction.deal_number || transaction.dealNumber || '',
      isin: transaction.isin_number || transaction.isin || '',
      faceValue: transaction.face_value ?? transaction.faceValue ?? '',
      yield: transaction.discount_rate_pct ?? transaction.discountRatePercent ?? transaction.yield ?? '',
      cleanPrice: transaction.clean_price ?? transaction.cleanPrice ?? '',
      dirtyPrice: transaction.dirty_price ?? transaction.dirtyPrice ?? '',
      broker: transaction.broker_id ?? transaction.broker ?? '',
      portfolio: transaction.portfolio_id ?? transaction.portfolio ?? 'Sherwood',
      strategy: transaction.strategy_id ?? transaction.strategy ?? 'Sherwood',
      custodian: transaction.custodian || 'Seylan Bank',
      settlementMode: transaction.settlement_mode || transaction.settlementMode || '',
      brokerage: transaction.brokerage ?? '',
      currency: transaction.currency || 'LKR',
      settlementAmount: transaction.settlement_amount ?? transaction.settlementAmount ?? '',
      issueDate: toInputDate(transaction.issue_date || transaction.issueDate),
      maturityDate: toInputDate(transaction.maturity_date || transaction.maturityDate),
      priceCalculationFormula: transaction.formula_text || transaction.priceCalculationFormula || ''
    });
  };

  const handleViewTbillDeal = (transaction) => {
    setEditingTransactionId(null);
    setEditingTransaction(null);
    populateTbillFormFromTransaction(transaction);
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    fetchRecentTransactions();
  }, []);

  useEffect(() => {
    const p = computeTbillPricing({
      valueDateYmd: form.valueDate,
      maturityDateYmd: form.maturityDate,
      faceValue: form.faceValue,
      annualDiscountRatePercent: form.yield
    });
    if (!p.ok) {
      setForm((prev) => ({
        ...prev,
        cleanPrice: '',
        dirtyPrice: '',
        settlementAmount: '',
        priceCalculationFormula: p.error ? `Cannot price: ${p.error}` : ''
      }));
      return;
    }
    const { cashPrice, pricePer100, days } = p;
    setForm((prev) => ({
      ...prev,
      cleanPrice: pricePer100.toFixed(4),
      dirtyPrice: pricePer100.toFixed(4),
      settlementAmount: String(Math.round(cashPrice)),
      priceCalculationFormula: [
        `T-Bill (ACT/364): Price = FV / (1 + (r × days / 364))`,
        `FV=${prev.faceValue} (maturity); r=${prev.yield}% p.a.; days=${days} (value date → maturity)`,
        `Price per 100=${pricePer100.toFixed(4)}; Cash price (settlement)=${Math.round(cashPrice)} (rounded up to ones)`,
        `Discount (FV − cash)=${(Number(prev.faceValue) - cashPrice).toFixed(4)}`
      ].join(' | ')
    }));
  }, [form.valueDate, form.maturityDate, form.faceValue, form.yield]);

  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      setIsLoading(true);

      const counterpartyData = await counterpartyApi.getAll();
      setCounterparties(counterpartyData);

      const [brokersRes, portfoliosRes, strategiesRes, settlementModesRes, isinRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/brokers`),
        axios.get(`${API_BASE_URL}/portfolio-master`),
        axios.get(`${API_BASE_URL}/strategy-master`),
        axios.get(`${API_BASE_URL}/payment-master/modes`).then((r) => r.data?.data || []).catch(() => []),
        axios.get(`${API_BASE_URL}/isin-master`)
      ]);

      setBrokers(Array.isArray(brokersRes.data) ? brokersRes.data : []);
      const portData = portfoliosRes.data || [];
      setPortfolios(
        portData.map((p) => ({
          id: p.portfolio_id,
          name: p.portfolio_name
        }))
      );
      const stratData = strategiesRes.data || [];
      setStrategies(
        stratData.map((s) => ({
          id: s.strategy_id,
          name: s.strategy_id + (s.strategy_type ? ` (${s.strategy_type})` : '')
        }))
      );

      const modes = Array.isArray(settlementModesRes) ? settlementModesRes : [];
      setSettlementModes(modes);

      const isins = isinRes.data?.data || isinRes.data || [];
      setIsinList(Array.isArray(isins) ? isins : []);

      setForm((prev) => {
        let next = { ...prev };
        // Default Portfolio = Sherwood (id-literal first, fuzzy match fallback)
        if ((!prev.portfolio || prev.portfolio === 'Sherwood') && portData.length) {
          const literal = portData.find((p) => String(p.portfolio_id) === 'Sherwood');
          const fuzzy = literal ||
            portData.find((p) => `${p.portfolio_id || ''} ${p.portfolio_name || ''}`.toLowerCase().includes('sherwood'));
          if (fuzzy) next.portfolio = fuzzy.portfolio_id;
        }
        // Default Strategy = Sherwood (trading) - matches strategy_id "Sherwood"
        if ((!prev.strategy || prev.strategy === 'Sherwood') && stratData.length) {
          const literal = stratData.find((s) => String(s.strategy_id) === 'Sherwood');
          const fuzzy = literal ||
            stratData.find((s) => `${s.strategy_id || ''} ${s.strategy_type || ''}`.toLowerCase().includes('sherwood'));
          if (fuzzy) next.strategy = fuzzy.strategy_id;
        }
        if (!prev.settlementMode && modes.length > 0) {
          const containsSeylanBank = (m) => {
            const haystack = [
              m.payment_method_owner, m.owner, m.bank_name, m.bank,
              m.payment_method, m.bank_payment_code
            ].map((v) => String(v || '').toLowerCase()).join(' ');
            return haystack.includes('seylan bank');
          };
          const defaultMode =
            modes.find(containsSeylanBank) ||
            modes.find((m) => (m.bank_payment_code || '').toLowerCase().includes('seylan1')) ||
            modes.find((m) => (m.payment_method || '').toLowerCase().includes('cbsl')) ||
            modes[0];
          if (defaultMode?.bank_payment_code) {
            next.settlementMode = defaultMode.bank_payment_code;
            fetchBankDetailsByCode(defaultMode.bank_payment_code)
              .then((details) => {
                if (details) {
                  setSelectedBankDetails({
                    bank_name: details.bank_name || '',
                    bank_account_number: details.bank_account_number || '',
                    bank_branch: details.bank_branch || ''
                  });
                }
              })
              .catch(() => {});
          }
        }
        return next;
      });
    } catch (err) {
      console.error('Error loading dropdown data:', err);
      setError('Failed to load form data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Reject out-of-range / malformed dates (e.g. a 5-digit year) for date fields.
    if (['tradeDate', 'valueDate', 'issueDate', 'maturityDate'].includes(field) && !isDateInRange(value)) {
      return;
    }
    if (field === 'transactionType' && value === 'Sell') {
      if (!form.isin) {
        setError('Please select an ISIN before selling.');
        setForm((prev) => ({ ...prev, transactionType: '' }));
        return;
      }
      setShowSellModal(true);
      setForm((prev) => ({ ...prev, transactionType: value }));
      setError('');
      setSuccess('');
      return;
    }

    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleSellModalPortfolioChange = (portfolio) => {
    if (portfolio) {
      setPortfolioFromSellModal(true);
      setForm((f) => ({ ...f, portfolio }));
    }
  };

  const handleSellDealsConfirm = ({ deals, portfolio, updateFaceValue }) => {
    setSellDeals(deals);
    setShowSellModal(false);
    const faceValueToUse =
      updateFaceValue || deals.reduce((sum, d) => sum + (Number(d.amountToSell) || 0), 0);
    if (portfolio) {
      setPortfolioFromSellModal(true);
    }
    setForm((f) => ({
      ...f,
      faceValue: faceValueToUse ? String(faceValueToUse) : '',
      portfolio: portfolio || f.portfolio
    }));
    setSuccess(`${deals.length} T-Bill holding(s) selected for sell. Complete the form and submit.`);
    setError('');
  };

  const handleIsinChange = (e) => {
    const isinNumber = e.target.value;
    const selected = isinList.find((i) => i.isin_number === isinNumber);
    setForm((prev) => ({
      ...prev,
      isin: isinNumber,
      issueDate: selected ? toInputDate(selected.issue_date) : '',
      maturityDate: selected ? toInputDate(selected.maturity_date) : ''
    }));
    setError('');
    setSuccess('');
  };

  const handleSettlementModeChange = (e) => {
    const code = e.target.value;
    handleInputChange('settlementMode', code);
    if (code) {
      fetchBankDetailsByCode(code)
        .then((details) => {
          if (details) {
            setSelectedBankDetails({
              bank_name: details.bank_name || '',
              bank_account_number: details.bank_account_number || '',
              bank_branch: details.bank_branch || ''
            });
          } else {
            setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
          }
        })
        .catch(() => setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' }));
    } else {
      setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
    }
  };

  const handleEditRejectedDeal = (transaction) => {
    setEditingTransaction(transaction);
    setEditingTransactionId(transaction.id);
    populateTbillFormFromTransaction(transaction);
    setError('');
    setSuccess('Editing rejected deal. Review and modify the fields below, then click Submit to resubmit for approval.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.faceValue || !form.yield || !form.isin) {
      setError('Please fill Face value at maturity, Annual discount rate (%), and ISIN before submitting');
      return;
    }
    if (form.transactionType === 'Sell' && (!sellDeals || sellDeals.length === 0)) {
      setError('Please select holdings to sell using the sell modal.');
      setShowSellModal(true);
      return;
    }
    if (!form.maturityDate || !form.valueDate) {
      setError('Value date and maturity date are required');
      return;
    }
    if (!pricing.ok) {
      setError(pricing.error || 'Invalid pricing inputs');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const formData = {
        ...form,
        daysToMaturity: pricing.days,
        pricePer100: pricing.pricePer100,
        settlementAmount: String(Math.round(pricing.cashPrice)),
        cleanPrice: pricing.pricePer100.toFixed(4),
        dirtyPrice: pricing.pricePer100.toFixed(4),
        discountRatePercent: form.yield,
        userId: user?.id,
        current_approval_level: 'front_office',
        status: 'pending',
        priceCalculationFormula: form.priceCalculationFormula,
        ...(form.transactionType === 'Sell' && sellDeals.length > 0 ? { sell_deals: sellDeals } : {})
      };

      if (editingTransactionId) {
        // The deal is identified by its id in the PUT URL, so a deal number is
        // not strictly required (older rejected deals may predate the column).
        // Preserve it when available so it isn't lost on resubmit.
        const originalDealNumber =
          form.dealNumber || editingTransaction?.deal_number || editingTransaction?.dealNumber || null;

        const updateData = {
          ...formData,
          ...(originalDealNumber ? { dealNumber: originalDealNumber } : {})
        };

        await axios.put(`${API_BASE_URL}/tbill/${editingTransactionId}`, updateData);
        setSuccess(
          originalDealNumber
            ? `Deal ${originalDealNumber} updated and resubmitted for approval successfully!`
            : 'Deal updated and resubmitted for approval successfully!'
        );
        setEditingTransactionId(null);
        setEditingTransaction(null);
        setForm(getInitialFormState());
        fetchRecentTransactions();
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/tbill`, formData);
      const isSell = form.transactionType === 'Sell';
      const legs = response.data?.data?.legs || [];
      const dealId =
        response.data?.data?.id ||
        response.data?.id ||
        response.data?.data?.insertId;
      const dealNumber =
        response.data?.data?.deal_number ||
        response.data?.data?.dealNumber ||
        '';
      const limitWarning = response.data?.limitWarning || response.data?.data?.limitWarning;
      const cpLimitWarning = response.data?.counterpartyLimitWarning || response.data?.data?.counterpartyLimitWarning;
      // Both limits can breach on the same deal, so append whichever fired.
      const limitWarningSuffix =
        (limitWarning ? ` This deal exceeds your dealer limit — Middle Office has been notified.` : '') +
        (cpLimitWarning ? ` ${cpLimitWarning.message} Middle Office has been notified.` : '');

      if (isSell) {
        setSuccess(
          (legs.length > 1
            ? `${legs.length} T-Bill sell transactions submitted for approval.`
            : dealNumber
              ? `T-Bill sell submitted successfully! Deal number: ${dealNumber}`
              : 'T-Bill sell submitted successfully!') + limitWarningSuffix
        );
        setSellDeals([]);
        setPortfolioFromSellModal(false);
        setForm(getInitialFormState());
        fetchRecentTransactions();
        return;
      }

      setCreatedTbillDealId(dealId);
      setCreatedDealNumber(dealNumber);
      setShowTbillDocumentSection(true);

      setSuccess(
        (dealNumber
          ? `T-Bill transaction submitted successfully! Deal number: ${dealNumber}`
          : 'T-Bill transaction submitted successfully!') + limitWarningSuffix
      );
      setForm(getInitialFormState());
      fetchRecentTransactions();
    } catch (err) {
      console.error('Error submitting T-Bill transaction:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to submit transaction');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setForm(getInitialFormState());
    setError('');
    setSuccess('');
    setEditingTransactionId(null);
    setEditingTransaction(null);
    setSellDeals([]);
    setPortfolioFromSellModal(false);
    setShowSellModal(false);
    setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
  };

  const rejectedTransactions = recentTransactions.filter((tx) => tx.status === 'rejected');

  const canEditRejectedTbill = (tx) => String(tx?.status || '').toLowerCase() === 'rejected';

  const daysDisplay = pricing.ok ? pricing.days : pricing.daysToMaturity != null ? pricing.days : '—';

  const fmtMoney = (v) =>
    Number.isFinite(Number(v))
      ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '—';
  const fmtPrice = (v) =>
    Number.isFinite(Number(v))
      ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
      : '—';

  const getDerivedFieldStyle = (txnType) => {
    if (txnType === 'Buy') {
      return {
        backgroundColor: '#dcfce7',
        border: '1.5px solid #16a34a',
        color: '#065f46',
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      };
    }
    if (txnType === 'Sell') {
      return {
        backgroundColor: '#fee2e2',
        border: '1.5px solid #dc2626',
        color: '#7f1d1d',
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      };
    }
    return {
      backgroundColor: '#ffeeba',
      fontWeight: 'bold',
      transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
    };
  };

  const memoCounterparties = useMemo(() => {
    const individual = counterparties.filter((cp) => cp.type === 'individual');
    const joint = counterparties.filter((cp) => cp.type === 'joint');
    const corporate = counterparties.filter((cp) => cp.type === 'corporate');
    return (
      <>
        {individual.length > 0 && (
          <optgroup label="Individual">
            {individual.map((cp) => (
              <option key={cp.unique_id || cp.id} value={cp.unique_id || cp.id}>
                {cp.short_name || cp.name}
              </option>
            ))}
          </optgroup>
        )}
        {joint.length > 0 && (
          <optgroup label="Joint">
            {joint.map((cp) => (
              <option key={cp.unique_id || cp.id} value={cp.unique_id || cp.id}>
                {cp.short_name || cp.name}
              </option>
            ))}
          </optgroup>
        )}
        {corporate.length > 0 && (
          <optgroup label="Corporate">
            {corporate.map((cp) => (
              <option key={cp.unique_id || cp.id} value={cp.unique_id || cp.id}>
                {cp.short_name || cp.name}
              </option>
            ))}
          </optgroup>
        )}
      </>
    );
  }, [counterparties]);

  return (
    <div className="tbill-page-container">
      <TbillSellDealModal
        show={showSellModal}
        onClose={() => setShowSellModal(false)}
        onConfirm={handleSellDealsConfirm}
        onPortfolioChange={handleSellModalPortfolioChange}
        isin={form.isin}
        tbillFaceValue={form.faceValue}
        pricePer100={form.cleanPrice}
        formValueDate={form.valueDate}
      />
      <div className="content">
        <div className="tbill-form-container">
          <h2>T-Bill Transaction Entry</h2>

          {form.dealNumber && (
            <div className="tbill-form-help-text info" style={{ marginBottom: 12 }}>
              Deal Number: <strong>{form.dealNumber}</strong>
              {editingTransactionId ? ' (editing rejected deal)' : ''}
            </div>
          )}

          {error && <div className="tbill-error-message">{error}</div>}

          {success && <div className="tbill-success-message">{success}</div>}

          <form onSubmit={handleSubmit} className="tbill-form-grid">
            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="tradeDate">Trade Date</label>
                <input
                  type="date"
                  id="tradeDate"
                  value={form.tradeDate}
                  onChange={(e) => handleInputChange('tradeDate', e.target.value)}
                  min={DATE_INPUT_MIN}
                  max={DATE_INPUT_MAX}
                  className="form-input"
                  required
                />
              </div>
              <div className="tbill-form-group">
                <label htmlFor="valueDate">Value Date</label>
                <input
                  type="date"
                  id="valueDate"
                  value={form.valueDate}
                  onChange={(e) => handleInputChange('valueDate', e.target.value)}
                  min={DATE_INPUT_MIN}
                  max={DATE_INPUT_MAX}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="faceValue">Face value at maturity (FV)</label>
                <input
                  type="text"
                  id="faceValue"
                  value={form.faceValue && !isNaN(Number(form.faceValue)) ? Number(form.faceValue).toLocaleString(undefined) : form.faceValue}
                  onChange={(e) => handleInputChange('faceValue', e.target.value.replace(/,/g, ''))}
                  className="form-input"
                  inputMode="numeric"
                  required
                />
                <div className="tbill-form-help-text info">Amount received at maturity (notional).</div>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="yield">Annual discount rate (%)</label>
                <input
                  type="number"
                  id="yield"
                  value={form.yield}
                  onChange={(e) => handleInputChange('yield', e.target.value)}
                  className="form-input"
                  step="0.0001"
                  min="0"
                  required
                />
                <div className="tbill-form-help-text info">e.g. 10 for 10% (0.10 as decimal in formula).</div>
              </div>
            </div>

            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="isin">ISIN</label>
                <select
                  id="isin"
                  value={form.isin}
                  onChange={handleIsinChange}
                  className="form-select"
                  required
                >
                  <option value="">Select ISIN</option>
                  {(() => {
                    const tbills = isinList.filter((i) => (i.isin_number || '').toUpperCase().startsWith('LKA'));
                    const tbonds = isinList.filter((i) => (i.isin_number || '').toUpperCase().startsWith('LKB'));
                    const others = isinList.filter((i) => {
                      const n = (i.isin_number || '').toUpperCase();
                      return !n.startsWith('LKA') && !n.startsWith('LKB');
                    });
                    const renderOption = (i, idx) => (
                      <option key={`${i.isin_number || 'isin'}-${i.id ?? idx}`} value={i.isin_number}>
                        {i.isin_number} — {i.isin_issuer || ''}
                      </option>
                    );
                    return (
                      <>
                        {tbills.length > 0 && (
                          <optgroup label="T-Bill (LKA)">
                            {tbills.map((i, idx) => renderOption(i, idx))}
                          </optgroup>
                        )}
                        {tbonds.length > 0 && (
                          <optgroup label="T-Bond (LKB)">
                            {tbonds.map((i, idx) => renderOption(i, idx))}
                          </optgroup>
                        )}
                        {others.length > 0 && (
                          <optgroup label="Other">
                            {others.map((i, idx) => renderOption(i, idx))}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
              </div>
            </div>

            <div className="tbill-form-row three-column">
              <div className="tbill-form-group">
                <label>Days to maturity</label>
                <input type="text" className="form-input" readOnly value={daysDisplay === '—' ? '' : String(daysDisplay)} placeholder="—" style={getDerivedFieldStyle(form.transactionType)} />
                <div className="tbill-form-help-text info">ACT calendar days from value date to ISIN maturity.</div>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="issueDate">Issue Date</label>
                <input
                  type="date"
                  id="issueDate"
                  value={form.issueDate}
                  onChange={(e) => handleInputChange('issueDate', e.target.value)}
                  min={DATE_INPUT_MIN}
                  max={DATE_INPUT_MAX}
                  className="form-input"
                  style={form.isin ? getDerivedFieldStyle(form.transactionType) : undefined}
                />
                <div className="tbill-form-help-text info">From ISIN master (editable if needed).</div>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="maturityDate">Maturity Date</label>
                <input
                  type="date"
                  id="maturityDate"
                  value={form.maturityDate}
                  onChange={(e) => handleInputChange('maturityDate', e.target.value)}
                  min={DATE_INPUT_MIN}
                  max={DATE_INPUT_MAX}
                  className="form-input"
                  required
                  style={form.isin ? getDerivedFieldStyle(form.transactionType) : undefined}
                />
                <div className="tbill-form-help-text info">From ISIN master; drives day count.</div>
              </div>
            </div>

            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="transactionType">Transaction Type</label>
                <select
                  id="transactionType"
                  value={form.transactionType}
                  onChange={(e) => handleInputChange('transactionType', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Type</option>
                  {transactionTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="counterparty">Counterparty</label>
                <select
                  id="counterparty"
                  value={form.counterparty}
                  onChange={(e) => handleInputChange('counterparty', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Counterparty</option>
                  {memoCounterparties}
                </select>
              </div>
            </div>

            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="broker">Broker</label>
                <select
                  id="broker"
                  value={form.broker}
                  onChange={(e) => handleInputChange('broker', e.target.value)}
                  className="form-select"
                >
                  <option value="">Select Broker</option>
                  {brokers.map((broker) => (
                    <option key={broker.id} value={broker.id}>
                      {broker.broker_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="portfolio">Portfolio</label>
                <select
                  id="portfolio"
                  value={form.portfolio}
                  onChange={(e) => handleInputChange('portfolio', e.target.value)}
                  className="form-select"
                  disabled={portfolioFromSellModal}
                >
                  <option value="">Select Portfolio</option>
                  {portfolios.map((portfolio) => (
                    <option key={portfolio.id} value={portfolio.id}>
                      {portfolio.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="strategy">Strategy</label>
                <select
                  id="strategy"
                  value={form.strategy}
                  onChange={(e) => handleInputChange('strategy', e.target.value)}
                  className="form-select"
                >
                  <option value="">Select Strategy</option>
                  {strategies.map((strategy) => (
                    <option key={strategy.id} value={strategy.id}>
                      {strategy.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="custodian">Custodian</label>
                <input
                  type="text"
                  id="custodian"
                  value={form.custodian}
                  onChange={(e) => handleInputChange('custodian', e.target.value)}
                  className="form-input"
                  placeholder="Enter Custodian"
                />
              </div>
            </div>

            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="settlementMode">Settlement Mode</label>
                <select
                  id="settlementMode"
                  value={form.settlementMode}
                  onChange={handleSettlementModeChange}
                  className="form-select"
                >
                  <option value="">Select Settlement Mode</option>
                  {settlementModes.map((mode) => (
                    <option key={mode.bank_payment_code || mode.payment_method} value={mode.bank_payment_code || ''}>
                      {mode.payment_method} ({mode.bank_payment_code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="brokerage">Brokerage</label>
                <input
                  type="number"
                  id="brokerage"
                  value={form.brokerage}
                  onChange={(e) => handleInputChange('brokerage', e.target.value)}
                  className="form-input"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            {form.settlementMode && (
              <div className="tbill-form-row three-column">
                <div className="tbill-form-group">
                  <label>Settlement Bank</label>
                  <input className="form-input" readOnly value={selectedBankDetails.bank_name || ''} style={getDerivedFieldStyle(form.transactionType)} />
                </div>
                <div className="tbill-form-group">
                  <label>Bank Branch</label>
                  <input className="form-input" readOnly value={selectedBankDetails.bank_branch || ''} style={getDerivedFieldStyle(form.transactionType)} />
                </div>
                <div className="tbill-form-group">
                  <label>Bank Account Number</label>
                  <input className="form-input" readOnly value={selectedBankDetails.bank_account_number || ''} style={getDerivedFieldStyle(form.transactionType)} />
                </div>
              </div>
            )}

            <div className="tbill-form-row single-column">
              <div className="tbill-form-group">
                <label htmlFor="cleanPrice">Price (per 100)</label>
                <input
                  type="text"
                  id="cleanPrice"
                  value={form.cleanPrice ? fmtPrice(form.cleanPrice) : ''}
                  className="form-input"
                  readOnly
                  style={getDerivedFieldStyle(form.transactionType)}
                />
              </div>
            </div>

            <div className="tbill-form-row">
              <div className="tbill-form-group">
                <label htmlFor="currency">Currency</label>
                <select
                  id="currency"
                  value={form.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="form-select"
                >
                  {currencyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tbill-form-group">
                <label htmlFor="settlementAmount">Cash price / Settlement amount</label>
                <input type="text" id="settlementAmount" value={form.settlementAmount ? fmtMoney(form.settlementAmount) : ''} className="form-input" readOnly style={getDerivedFieldStyle(form.transactionType)} />
              </div>
            </div>

            <div className="tbill-form-row single-column">
              <div className="tbill-form-group">
                <label htmlFor="dirtyPrice">Dirty price (per 100)</label>
                <input type="text" id="dirtyPrice" value={form.dirtyPrice ? fmtPrice(form.dirtyPrice) : ''} className="form-input" readOnly style={getDerivedFieldStyle(form.transactionType)} />
                <div className="tbill-form-help-text info">No coupon on T-bills; same as clean price per 100.</div>
              </div>
            </div>

            <div className="tbill-form-row single-column">
              <div className="tbill-pricing-summary" aria-live="polite">
                <div className="tbill-pricing-summary__head">
                  <span className="tbill-pricing-summary__title">Pricing Summary</span>
                  <span className="tbill-pricing-summary__badge">ACT/364</span>
                </div>

                {pricing.ok ? (
                  <>
                    <div className="tbill-pricing-summary__formula">
                      Price = FV / (1 + (r × days / 364))
                    </div>

                    <div className="tbill-pricing-summary__grid">
                      <div className="tbill-pricing-item">
                        <span className="tbill-pricing-item__label">Face Value (FV)</span>
                        <span className="tbill-pricing-item__value">{fmtMoney(form.faceValue)}</span>
                      </div>
                      <div className="tbill-pricing-item">
                        <span className="tbill-pricing-item__label">Discount Rate (r)</span>
                        <span className="tbill-pricing-item__value">
                          {form.yield !== '' ? `${form.yield}%` : '—'}
                        </span>
                      </div>
                      <div className="tbill-pricing-item">
                        <span className="tbill-pricing-item__label">Days to Maturity</span>
                        <span className="tbill-pricing-item__value">{pricing.days}</span>
                      </div>
                      <div className="tbill-pricing-item">
                        <span className="tbill-pricing-item__label">Price per 100</span>
                        <span className="tbill-pricing-item__value">{fmtPrice(pricing.pricePer100)}</span>
                      </div>
                      <div className="tbill-pricing-item tbill-pricing-item--accent">
                        <span className="tbill-pricing-item__label">Cash Price (Settlement)</span>
                        <span className="tbill-pricing-item__value">{fmtMoney(pricing.cashPrice)}</span>
                      </div>
                      <div className="tbill-pricing-item">
                        <span className="tbill-pricing-item__label">Discount (FV − Cash)</span>
                        <span className="tbill-pricing-item__value">{fmtMoney(pricing.discountEarned)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="tbill-pricing-summary__empty">
                    {pricing.error
                      ? `Cannot price: ${pricing.error}`
                      : 'Enter face value, discount rate, value date and maturity date to see pricing.'}
                  </div>
                )}
              </div>
            </div>

            <div className="tbill-form-actions">
              <button type="button" onClick={handleReset} className="tbill-btn tbill-btn-secondary" disabled={isLoading}>
                Reset
              </button>
              <button type="submit" className="tbill-btn tbill-btn-primary" disabled={isLoading}>
                {isLoading
                  ? 'Submitting...'
                  : editingTransactionId
                    ? 'Resubmit Transaction'
                    : 'Submit Transaction'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="tbill-form-container" style={{ marginTop: 24 }}>
        <RecentDealsDateFilter
          title="Recent T-Bill Transactions"
          filterDateYmd={recentDealsFilterDate}
          onFilterDateChange={setRecentDealsFilterDate}
          onRefresh={fetchRecentTransactions}
          refreshing={isLoading || recentDealsFilterLoading}
        />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
            <thead style={{ background: '#f1f3f7' }}>
              <tr>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Value Date</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Deal Number</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Type</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>ISIN</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Counterparty</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>Face Value</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>Settlement</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Status</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecentTransactions.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 16, textAlign: 'center', color: '#666' }}>
                    {recentDealsFilterDate
                      ? `No T-Bill transactions found for ${recentDealsFilterDate}.`
                      : 'Loading transactions...'}
                  </td>
                </tr>
              ) : (
                filteredRecentTransactions.map((tx) => (
                  <tr key={tx.id}>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>
                      {toInputDate(tx.value_date || tx.valueDate)}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>
                      {tx.deal_number || tx.dealNumber}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>
                      {tx.transaction_type || tx.transactionType}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>
                      {tx.isin_number || tx.isin}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>
                      {tx.counterparty_name || tx.counterparty || '—'}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>
                      {fmtMoney(tx.face_value ?? tx.faceValue)}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>
                      {fmtMoney(tx.settlement_amount ?? tx.settlementAmount)}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>
                      {tx.status || 'pending'}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>
                      <button
                        type="button"
                        className="tbill-btn tbill-btn-secondary"
                        onClick={() => handleViewTbillDeal(tx)}
                        style={{ marginRight: 6 }}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="tbill-btn tbill-btn-secondary"
                        onClick={() => {
                          setSelectedTbillDealForDocuments(tx);
                          setShowTbillDocumentSection(false);
                        }}
                        style={{ marginRight: 6 }}
                      >
                        Docs
                      </button>
                      {canEditRejectedTbill(tx) && (
                        <button
                          type="button"
                          className="tbill-btn tbill-btn-primary"
                          onClick={() => handleEditRejectedDeal(tx)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rejectedTransactions.length > 0 && (
        <div className="tbill-form-container" style={{ marginTop: 24 }}>
          <h3>Rejected Deals — Edit &amp; Resubmit</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <thead style={{ background: '#f1f3f7' }}>
                <tr>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Deal Number</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Value Date</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>ISIN</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>Face Value</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Rejection Comment</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rejectedTransactions.map((tx) => (
                  <tr key={tx.id} style={{ background: '#fff5f5' }}>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>{tx.deal_number || tx.dealNumber}</td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>{toInputDate(tx.value_date || tx.valueDate)}</td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>{tx.isin_number || tx.isin}</td>
                    <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>
                      {Number(tx.face_value || tx.faceValue || 0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee', fontStyle: 'italic', color: '#7f1d1d' }}>
                      {tx.comment || '—'}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>
                      <button
                        type="button"
                        className="tbill-btn tbill-btn-primary"
                        onClick={() => handleEditRejectedDeal(tx)}
                      >
                        Edit &amp; Resubmit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(showTbillDocumentSection && createdTbillDealId) || selectedTbillDealForDocuments ? (
        <div
          style={{
            marginTop: '24px',
            width: '100%',
            padding: '20px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            background: '#fff'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0 }}>
              Documents
              {(createdDealNumber || createdTbillDealId || selectedTbillDealForDocuments?.deal_number) && (
                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                  - Deal #: {createdDealNumber || selectedTbillDealForDocuments?.deal_number || createdTbillDealId}
                </span>
              )}
            </h4>
            {selectedTbillDealForDocuments && (
              <button className="btn btn-sm btn-secondary" onClick={() => setSelectedTbillDealForDocuments(null)}>
                Close
              </button>
            )}
          </div>
          <DocumentUpload
            transactionType="tbill"
            transactionId={createdTbillDealId || selectedTbillDealForDocuments?.id || selectedTbillDealForDocuments?.deal_number}
            onUploadSuccess={() => {
              setShowTbillDocumentSection(false);
            }}
            onUploadError={(uploadErr) => {
              console.error('Failed to upload document:', uploadErr);
            }}
            multiple={true}
          />
          <DocumentList
            transactionType="tbill"
            transactionId={createdTbillDealId || selectedTbillDealForDocuments?.id || selectedTbillDealForDocuments?.deal_number}
            onDocumentDeleted={() => {}}
          />
        </div>
      ) : null}
    </div>
  );
};

export default FixedIncomeTbillPage;
